// Attachment service for retrieving email attachment content via EWS

export interface AttachmentInfo {
  id: string;
  name: string;
  contentType: string;
  size: number;
  isInline: boolean;
}

export interface AttachmentContent {
  name: string;
  contentType: string;
  content: string; // base64 encoded
  size: number;
}

// Supported MIME types for documents
export const SUPPORTED_DOCUMENT_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // DOCX
  'text/plain',
  'text/html',
  'application/vnd.oasis.opendocument.text', // ODT
  'application/rtf',
  'application/epub+zip',
];

// Supported MIME types for spreadsheets
export const SUPPORTED_SPREADSHEET_TYPES = [
  'text/csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // XLSX
  'application/vnd.ms-excel.sheet.macroEnabled.12', // XLSM
  'application/json',
  'text/tab-separated-values',
];

// Supported MIME types for images
export const SUPPORTED_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
];

// Maximum attachment size (25MB conservative limit for Claude)
export const MAX_ATTACHMENT_SIZE = 25 * 1024 * 1024;

// Maximum number of attachments to process
export const MAX_ATTACHMENTS = 10;

// Custom error class for EWS unsupported accounts
export class EwsUnsupportedError extends Error {
  constructor() {
    super('Attachment analysis requires Microsoft 365 Business or Enterprise. Personal accounts (@outlook.com, @hotmail.com) are not supported.');
    this.name = 'EwsUnsupportedError';
  }
}

// Check if EWS is available (only works with M365 Business/Enterprise)
export function isEwsSupported(): boolean {
  try {
    // makeEwsRequestAsync exists but may fail at runtime for personal accounts
    return typeof Office.context.mailbox.makeEwsRequestAsync === 'function';
  } catch {
    return false;
  }
}

// Check if a content type is supported
export function isSupportedAttachment(contentType: string): boolean {
  const lowerType = contentType.toLowerCase();
  return [...SUPPORTED_DOCUMENT_TYPES, ...SUPPORTED_SPREADSHEET_TYPES, ...SUPPORTED_IMAGE_TYPES].some(
    (type) => lowerType.includes(type) || lowerType.includes(type.split('/')[1])
  );
}

// Determine attachment category for Claude API
export function getAttachmentCategory(contentType: string): 'document' | 'image' | 'unsupported' {
  const lowerType = contentType.toLowerCase();

  if (SUPPORTED_IMAGE_TYPES.some((t) => lowerType.includes(t) || lowerType.includes(t.split('/')[1]))) {
    return 'image';
  }

  if (
    [...SUPPORTED_DOCUMENT_TYPES, ...SUPPORTED_SPREADSHEET_TYPES].some(
      (t) => lowerType.includes(t) || lowerType.includes(t.split('/')[1])
    )
  ) {
    return 'document';
  }

  return 'unsupported';
}

// Get attachment metadata using Office.js
export async function getAttachmentMetadata(): Promise<AttachmentInfo[]> {
  const item = Office.context.mailbox.item;
  if (!item) throw new Error('No email selected');

  // Check if getAttachmentsAsync is available (requires Mailbox 1.8, not supported on personal accounts)
  if (typeof item.getAttachmentsAsync !== 'function') {
    throw new EwsUnsupportedError();
  }

  return new Promise((resolve, reject) => {
    item.getAttachmentsAsync((result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        // Cast to AttachmentDetails since we're in read mode (MessageRead context)
        const attachments: AttachmentInfo[] = (result.value as Office.AttachmentDetails[]).map((att) => ({
          id: att.id,
          name: att.name,
          contentType: att.contentType,
          size: att.size,
          isInline: att.isInline,
        }));
        resolve(attachments);
      } else {
        reject(new Error(result.error?.message || 'Failed to get attachments'));
      }
    });
  });
}

// Build EWS GetAttachment request XML
function buildGetAttachmentRequest(attachmentId: string): string {
  // Escape XML special characters in attachment ID
  const escapedId = attachmentId
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

  return `<?xml version="1.0" encoding="utf-8"?>
<soap:Envelope xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
               xmlns:xsd="http://www.w3.org/2001/XMLSchema"
               xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/"
               xmlns:t="http://schemas.microsoft.com/exchange/services/2006/types">
  <soap:Header>
    <t:RequestServerVersion Version="Exchange2013"/>
  </soap:Header>
  <soap:Body>
    <GetAttachment xmlns="http://schemas.microsoft.com/exchange/services/2006/messages"
                   xmlns:t="http://schemas.microsoft.com/exchange/services/2006/types">
      <AttachmentIds>
        <t:AttachmentId Id="${escapedId}"/>
      </AttachmentIds>
    </GetAttachment>
  </soap:Body>
</soap:Envelope>`;
}

// Parse EWS GetAttachment response to extract base64 content
function parseAttachmentResponse(responseXml: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(responseXml, 'text/xml');

  // Check for SOAP fault - indicates EWS not supported (personal accounts)
  const fault = doc.querySelector('Fault');
  if (fault) {
    throw new EwsUnsupportedError();
  }

  // Check for response code error - also indicates EWS issues
  const responseCode = doc.querySelector('ResponseCode');
  if (responseCode && responseCode.textContent !== 'NoError') {
    throw new EwsUnsupportedError();
  }

  // Extract base64 content from the response
  const content = doc.querySelector('Content');
  if (!content || !content.textContent) {
    throw new EwsUnsupportedError();
  }

  return content.textContent;
}

// Get attachment content via EWS makeEwsRequestAsync
export async function getAttachmentContent(attachmentId: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const request = buildGetAttachmentRequest(attachmentId);

    try {
      Office.context.mailbox.makeEwsRequestAsync(request, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          try {
            const content = parseAttachmentResponse(result.value);
            resolve(content);
          } catch (e) {
            reject(e);
          }
        } else {
          // Check for common personal account error messages
          const errorMsg = result.error?.message || '';
          if (
            errorMsg.includes('not supported') ||
            errorMsg.includes('permission') ||
            errorMsg.includes('Ews') ||
            result.error?.code === 9020 // GenericResponseError often indicates unsupported
          ) {
            reject(new EwsUnsupportedError());
          } else {
            reject(new Error(errorMsg || 'EWS request failed'));
          }
        }
      });
    } catch (e) {
      // If makeEwsRequestAsync throws directly, it's not supported
      reject(new EwsUnsupportedError());
    }
  });
}

export interface AttachmentResult {
  attachments: AttachmentContent[];
  skipped: Array<{ name: string; reason: string }>;
}

// Get all supported attachments with their content
export async function getSupportedAttachmentsWithContent(): Promise<AttachmentResult> {
  const metadata = await getAttachmentMetadata();

  const result: AttachmentResult = {
    attachments: [],
    skipped: [],
  };

  // Filter to supported, non-inline, reasonably-sized attachments
  const candidates = metadata.filter((att) => !att.isInline);

  // Limit the number of attachments
  const toProcess = candidates.slice(0, MAX_ATTACHMENTS);

  // Track skipped attachments beyond the limit
  if (candidates.length > MAX_ATTACHMENTS) {
    for (let i = MAX_ATTACHMENTS; i < candidates.length; i++) {
      result.skipped.push({
        name: candidates[i].name,
        reason: 'Exceeded maximum attachment limit',
      });
    }
  }

  for (const att of toProcess) {
    // Check if type is supported
    if (!isSupportedAttachment(att.contentType)) {
      result.skipped.push({
        name: att.name,
        reason: `Unsupported file type: ${att.contentType}`,
      });
      continue;
    }

    // Check size limit
    if (att.size > MAX_ATTACHMENT_SIZE) {
      result.skipped.push({
        name: att.name,
        reason: `File too large (${Math.round(att.size / 1024 / 1024)}MB > 25MB limit)`,
      });
      continue;
    }

    // Fetch the content
    try {
      const content = await getAttachmentContent(att.id);
      result.attachments.push({
        name: att.name,
        contentType: att.contentType,
        content,
        size: att.size,
      });
    } catch (e) {
      // Re-throw EwsUnsupportedError to let caller handle it
      if (e instanceof EwsUnsupportedError) {
        throw e;
      }
      console.warn(`Failed to retrieve attachment ${att.name}:`, e);
      result.skipped.push({
        name: att.name,
        reason: e instanceof Error ? e.message : 'Failed to retrieve',
      });
    }
  }

  return result;
}
